import React from 'react';
import { CalendarIcon, HandWaveIcon, EyeIcon, UserIcon } from '@revfluence/fresh-icons/regular/esm';
import { cn } from '@/shadcn/lib/utils';
import { formatNumber } from '@/applications/Social/utils/formatNumber';
import { formatDate } from '@/applications/Social/utils/shared/date-helpers';

export interface CardStatItem {
  /** Stat icon */
  icon: React.ReactNode;
  /** Stat value (number will be formatted) */
  value: number | string;
  /** Custom className for the stat */
  className?: string;
  /** Unique key for the stat */
  key?: string;
}

export interface CardStatTextItem {
  /** Text icon */
  icon: React.ReactNode;
  /** Text content */
  text: string;
  /** Custom className for the text */
  className?: string;
  /** Unique key for the text */
  key?: string;
}

export interface CardStatsSectionProps {
  /** Array of stat items to display */
  stats?: CardStatItem[];
  /** Array of text items to display (like date) */
  textItems?: CardStatTextItem[];
  /** Show date with calendar icon */
  showDate?: boolean;
  /** Date value (string or Date object) */
  date?: string | Date | null;
  /** Show impressions stat */
  showImpressions?: boolean;
  /** Impressions value */
  impressions?: number | null;
  /** Show engagements stat */
  showEngagements?: boolean;
  /** Engagements value */
  engagements?: number | null;
  /** Show followers stat */
  showFollowers?: boolean;
  /** Followers value */
  followers?: number | null;
  /** Layout style */
  layout?: 'space-between' | 'flex-end' | 'flex-start' | 'center';
  /** Additional CSS classes */
  className?: string;
  /** Custom children */
  children?: React.ReactNode;
}

/**
 * CardStatsSection
 * ----------------
 * Renders a flexible stats section for cards with date, numeric stats, and text items.
 * Supports various layouts and built-in common stats like impressions, engagements, etc.
 *
 * Features:
 * - Built-in common stats (impressions, engagements, followers)
 * - Date formatting with calendar icon
 * - Custom stat and text item arrays
 * - Flexible layout options
 * - Responsive stat display
 * - Number formatting for large values
 */
export const CardStatsSection = React.forwardRef<HTMLDivElement, CardStatsSectionProps>(
  ({
    stats = [],
    textItems = [],
    showDate = false,
    date,
    showImpressions = false,
    impressions,
    showEngagements = false,
    engagements,
    showFollowers = false,
    followers,
    layout = 'space-between',
    className,
    children,
    ...props
  }, ref) => {
    const layoutClasses = {
      'space-between': 'justify-between',
      'flex-end': 'justify-end',
      'flex-start': 'justify-start',
      'center': 'justify-center',
    };

    // Build text items array
    const allTextItems = [...textItems];
    if (showDate && date) {
      allTextItems.push({
        icon: <CalendarIcon className="h-4 w-4" />,
        text: formatDate(date),
        key: 'date',
      });
    }

    // Build stats array
    const allStats = [...stats];
    if (showImpressions && impressions != null) {
      allStats.push({
        icon: <EyeIcon className="h-4 w-4" />,
        value: impressions,
        key: 'impressions',
      });
    }
    if (showEngagements && engagements != null) {
      allStats.push({
        icon: <HandWaveIcon className="h-4 w-4" />,
        value: engagements,
        className: 'hidden sm:flex',
        key: 'engagements',
      });
    }
    if (showFollowers && followers != null) {
      allStats.push({
        icon: <UserIcon className="h-4 w-4" />,
        value: followers,
        key: 'followers',
      });
    }

    const hasContent = allTextItems.length > 0 || allStats.length > 0 || children;

    if (!hasContent) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn('flex items-center text-sm text-white', layoutClasses[layout], className)}
        {...props}
      >
        {/* Text Items (like date) */}
        {allTextItems.length > 0 && (
          <div className="flex items-center gap-4">
            {allTextItems.map((item, index) => (
              <div
                key={item.key || `text-${index}`}
                className={cn('flex items-center gap-1', item.className)}
              >
                {item.icon}
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        )}

        {/* Stats */}
        {allStats.length > 0 && (
          <div className="flex items-center gap-3">
            {allStats.map((stat, index) => (
              <div
                key={stat.key || `stat-${index}`}
                className={cn('flex items-center gap-1', stat.className)}
              >
                {stat.icon}
                <span>
                  {typeof stat.value === 'number' ? formatNumber(stat.value) : stat.value}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Custom Children */}
        {children}
      </div>
    );
  }
);

CardStatsSection.displayName = 'CardStatsSection';
